<template>
  <div class="frb">
    <div class="frb__card">
      <header class="frb__header">
        <i class="bi bi-people-fill frb__header-icon" aria-hidden="true" />
        <div>
          <h2 class="frb__title">درخواست دوستی</h2>
          <p class="frb__subtitle">جستجو کنید، پروفایل ببینید، بعد دوست شوید</p>
        </div>
      </header>

      <form class="frb__search" @submit.prevent="runSearch">
        <input
          v-model.trim="query"
          class="frb__input"
          type="search"
          maxlength="24"
          placeholder="نام کاربری را بنویسید"
          autocomplete="off"
          @input="onQueryInput"
        />
        <button type="submit" class="frb__search-btn" :disabled="searching || query.length < 2">
          جستجو
        </button>
      </form>
      <p v-if="searchError" class="frb__error">{{ searchError }}</p>

      <ul v-if="results.length" class="frb__list">
        <li v-for="item in results" :key="item.user.id" class="frb__row">
          <button type="button" class="frb__person" @click="openProfile(item.user.username)">
            <span class="frb__name">{{ personName(item.user) }}</span>
            <span class="frb__handle">@{{ item.user.username }}</span>
          </button>
          <button
            v-if="item.friendship_status === 'none'"
            type="button"
            class="frb__join"
            :disabled="busy"
            @click="sendRequest(item.user.username)"
          >
            درخواست
          </button>
          <span v-else class="frb__meta">{{ statusShort(item.friendship_status) }}</span>
        </li>
      </ul>

      <p v-if="actionError" class="frb__error">{{ actionError }}</p>

      <h3 class="frb__section">درخواست‌های رسیده</h3>
      <p v-if="loading && !overview.incoming.length" class="frb__empty">در حال بارگذاری…</p>
      <p v-else-if="!overview.incoming.length" class="frb__empty">درخواست معلقی ندارید.</p>
      <ul v-else class="frb__list">
        <li v-for="req in overview.incoming" :key="req.id" class="frb__row">
          <button type="button" class="frb__person" @click="openProfile(req.from_user.username)">
            <span class="frb__name">{{ personName(req.from_user) }}</span>
            <span class="frb__handle">@{{ req.from_user.username }}</span>
          </button>
          <div class="frb__actions">
            <button type="button" class="frb__join" :disabled="busy" @click="accept(req.id)">
              پذیرش
            </button>
            <button type="button" class="frb__remove" :disabled="busy" @click="decline(req.id)">
              رد
            </button>
          </div>
        </li>
      </ul>

      <h3 class="frb__section">دوستان</h3>
      <p v-if="!overview.friends.length" class="frb__empty">هنوز دوستی ندارید.</p>
      <ul v-else class="frb__list">
        <li v-for="row in overview.friends" :key="row.user.id" class="frb__row">
          <button type="button" class="frb__person" @click="openProfile(row.user.username)">
            <span class="frb__name">{{ personName(row.user) }}</span>
            <span class="frb__handle">@{{ row.user.username }}</span>
          </button>
          <button
            type="button"
            class="frb__join"
            :disabled="busy"
            title="دعوت به اسپیکینگ"
            @click="invite(row.user.username)"
          >
            اسپیکینگ
          </button>
        </li>
      </ul>
    </div>

    <PublicProfileModal
      :profile="openProfileData"
      :busy="busy"
      @close="openProfileData = null"
      @request="sendRequest"
      @accept="accept"
      @decline="decline"
      @invite="invite"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, reactive, ref } from "vue";
import {
  acceptFriendRequest,
  declineFriendRequest,
  getFriendsOverview,
  getPublicProfile,
  searchSocialProfiles,
  sendFriendRequest,
  sendSpeakingInvite,
} from "@/api/social";
import {
  createSpeakingRoom,
  listActiveSpeakingRooms,
  openSpeakingLobby,
  SpeakingRoomExistsError,
} from "@/api/speakingRooms";
import PublicProfileModal from "@/components/PublicProfileModal.vue";
import { handleError } from "@/services/errorHandler";
import type { FriendsOverview, PublicProfile, PublicUser } from "@/types";

const POLL_MS = 8000;

const query = ref("");
const searching = ref(false);
const searchError = ref<string | null>(null);
const actionError = ref<string | null>(null);
const results = ref<PublicProfile[]>([]);
const loading = ref(true);
const busy = ref(false);
const openProfileData = ref<PublicProfile | null>(null);
const overview = reactive<FriendsOverview>({
  incoming: [],
  outgoing: [],
  friends: [],
});

let pollId: ReturnType<typeof setInterval> | null = null;
let searchTimer: ReturnType<typeof setTimeout> | null = null;

function personName(user: PublicUser): string {
  return user.full_name?.trim() || user.username;
}

function statusShort(status: PublicProfile["friendship_status"]): string {
  if (status === "friends") return "دوست";
  if (status === "outgoing") return "ارسال‌شده";
  if (status === "incoming") return "درخواست";
  return "";
}

function onQueryInput() {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    if (query.value.length >= 2) void runSearch();
    else results.value = [];
  }, 280);
}

async function runSearch() {
  if (query.value.length < 2) return;
  searching.value = true;
  searchError.value = null;
  try {
    results.value = await searchSocialProfiles(query.value);
  } catch (e) {
    searchError.value = handleError(e, "FriendshipBox.search");
  } finally {
    searching.value = false;
  }
}

async function loadOverview(silent = false) {
  if (!silent) loading.value = true;
  try {
    const data = await getFriendsOverview();
    overview.incoming = data.incoming;
    overview.outgoing = data.outgoing;
    overview.friends = data.friends;
  } catch (e) {
    if (!silent) actionError.value = handleError(e, "FriendshipBox.overview");
  } finally {
    loading.value = false;
  }
}

async function openProfile(username: string) {
  actionError.value = null;
  try {
    openProfileData.value = await getPublicProfile(username);
  } catch (e) {
    actionError.value = handleError(e, "FriendshipBox.profile");
  }
}

async function sendRequest(username: string) {
  busy.value = true;
  actionError.value = null;
  try {
    await sendFriendRequest(username);
    await Promise.all([loadOverview(true), query.value ? runSearch() : Promise.resolve()]);
    if (openProfileData.value?.user.username === username) {
      openProfileData.value = await getPublicProfile(username);
    }
  } catch (e) {
    actionError.value = handleError(e, "FriendshipBox.request");
  } finally {
    busy.value = false;
  }
}

async function accept(requestId: number) {
  busy.value = true;
  actionError.value = null;
  try {
    await acceptFriendRequest(requestId);
    await loadOverview(true);
    if (openProfileData.value) {
      openProfileData.value = await getPublicProfile(openProfileData.value.user.username);
    }
  } catch (e) {
    actionError.value = handleError(e, "FriendshipBox.accept");
  } finally {
    busy.value = false;
  }
}

async function decline(requestId: number) {
  busy.value = true;
  actionError.value = null;
  try {
    await declineFriendRequest(requestId);
    await loadOverview(true);
    openProfileData.value = null;
  } catch (e) {
    actionError.value = handleError(e, "FriendshipBox.decline");
  } finally {
    busy.value = false;
  }
}

async function ensureRoomCode(): Promise<string> {
  const rooms = await listActiveSpeakingRooms();
  const mine = rooms.find((room) => room.is_mine);
  if (mine) return mine.room_code;
  try {
    const created = await createSpeakingRoom();
    return created.room_code;
  } catch (e) {
    if (e instanceof SpeakingRoomExistsError) return e.roomCode;
    throw e;
  }
}

async function invite(username: string) {
  busy.value = true;
  actionError.value = null;
  try {
    const code = await ensureRoomCode();
    await sendSpeakingInvite(username, code);
    openSpeakingLobby(code, "examiner");
    openProfileData.value = null;
  } catch (e) {
    actionError.value = handleError(e, "FriendshipBox.invite");
  } finally {
    busy.value = false;
  }
}

onMounted(() => {
  void loadOverview();
  pollId = setInterval(() => {
    void loadOverview(true);
  }, POLL_MS);
});

onBeforeUnmount(() => {
  if (pollId) clearInterval(pollId);
  if (searchTimer) clearTimeout(searchTimer);
});
</script>

<style scoped>
.frb {
  width: 100%;
  direction: rtl;
  font-family: Vazirmatn, system-ui, "Segoe UI", Tahoma, sans-serif;
}

.frb__card {
  background: #ffffff;
  border: 1px solid #ece4d9;
  border-radius: 24px;
  padding: 22px 20px 18px;
  box-shadow: 0 4px 24px rgba(76, 55, 42, 0.07);
  color: #4c372a;
}

.frb__header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
}

.frb__header-icon {
  font-size: 1.3rem;
  color: #6d5ef8;
  margin-top: 2px;
}

.frb__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
}

.frb__subtitle {
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: #8c8176;
}

.frb__search {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.frb__input {
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid #ece4d9;
  border-radius: 12px;
  font-family: inherit;
  font-size: 0.85rem;
}

.frb__search-btn,
.frb__join,
.frb__remove {
  padding: 6px 12px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
}

.frb__search-btn,
.frb__join {
  border: none;
  background: #6d5ef8;
  color: #fff;
}

.frb__remove {
  border: 1px solid #f0c7c2;
  background: #fff5f4;
  color: #b42318;
}

.frb__search-btn:disabled,
.frb__join:disabled,
.frb__remove:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.frb__section {
  margin: 14px 0 8px;
  font-size: 0.82rem;
  font-weight: 700;
}

.frb__empty {
  margin: 0 0 8px;
  font-size: 0.78rem;
  color: #8c8176;
  line-height: 1.7;
}

.frb__error {
  margin: 0 0 8px;
  font-size: 0.78rem;
  color: #b42318;
  line-height: 1.6;
}

.frb__list {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
  background: #fffdf8;
  border: 1px solid #ece4d9;
  border-radius: 14px;
  overflow: hidden;
  max-height: 180px;
  overflow-y: auto;
}

.frb__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #ece4d9;
}

.frb__row:last-child {
  border-bottom: none;
}

.frb__person {
  min-width: 0;
  border: none;
  background: none;
  text-align: right;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
}

.frb__name {
  display: block;
  font-size: 0.84rem;
  font-weight: 700;
  color: #4c372a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.frb__handle {
  display: block;
  direction: ltr;
  text-align: right;
  font-size: 0.72rem;
  color: #6d5ef8;
}

.frb__meta {
  font-size: 0.72rem;
  color: #8c8176;
  font-weight: 600;
}

.frb__actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
</style>
